import { useCallback, useEffect, useState } from "react";
import type { RefObject } from "react";

const DRAG_THRESHOLD_PX = 4;
const SCROLL_EDGE_PX = 4;

function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }

  return Boolean(target.closest("a, button, input, textarea, select, [role='button']"));
}

type UseTopListCarouselTrackResult = {
  canScrollLeft: boolean;
  canScrollRight: boolean;
  isDragging: boolean;
  onTrackScroll: () => void;
};

/**
 * Scroll-edge cues + grab-drag for the peek TopList carousel.
 */
export function useTopListCarouselTrack(
  trackRef: RefObject<HTMLDivElement | null>,
  hasMultiple: boolean,
  locationCount: number
): UseTopListCarouselTrackResult {
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(hasMultiple);
  const [isDragging, setIsDragging] = useState(false);

  const updateScrollEdges = useCallback(() => {
    const track = trackRef.current;
    if (!track || !hasMultiple) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const overflow = track.scrollWidth - track.clientWidth;
    // Before layout (jsdom / first paint) overflow can be 0 — assume more to the right at start.
    if (overflow <= 0) {
      setCanScrollLeft(false);
      setCanScrollRight(track.scrollLeft <= SCROLL_EDGE_PX);
      return;
    }

    setCanScrollLeft(track.scrollLeft > SCROLL_EDGE_PX);
    setCanScrollRight(overflow - track.scrollLeft > SCROLL_EDGE_PX);
  }, [hasMultiple, trackRef]);

  const onTrackScroll = useCallback(() => {
    updateScrollEdges();
  }, [updateScrollEdges]);

  useEffect(() => {
    if (!hasMultiple) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    updateScrollEdges();

    const track = trackRef.current;
    if (!track) {
      return;
    }

    const resizeObserver =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateScrollEdges);
    resizeObserver?.observe(track);

    return () => {
      resizeObserver?.disconnect();
    };
  }, [hasMultiple, locationCount, trackRef, updateScrollEdges]);

  // Mouse/pen drag-to-scroll (touch keeps native scrolling).
  useEffect(() => {
    const track = trackRef.current;
    if (!track || !hasMultiple) {
      return;
    }

    let pointerId: number | null = null;
    let startX = 0;
    let startScrollLeft = 0;
    let dragging = false;

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === "touch" || event.button !== 0) {
        return;
      }
      if (isInteractiveTarget(event.target)) {
        return;
      }

      pointerId = event.pointerId;
      startX = event.clientX;
      startScrollLeft = track.scrollLeft;
      dragging = false;
      track.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) {
        return;
      }

      const deltaX = event.clientX - startX;
      if (!dragging && Math.abs(deltaX) > DRAG_THRESHOLD_PX) {
        dragging = true;
        setIsDragging(true);
        track.style.scrollSnapType = "none";
      }

      if (dragging) {
        track.scrollLeft = startScrollLeft - deltaX;
        event.preventDefault();
      }
    };

    const endDrag = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) {
        return;
      }

      pointerId = null;
      if (dragging) {
        dragging = false;
        setIsDragging(false);
        track.style.scrollSnapType = "";
        updateScrollEdges();
      }
    };

    track.addEventListener("pointerdown", onPointerDown);
    track.addEventListener("pointermove", onPointerMove);
    track.addEventListener("pointerup", endDrag);
    track.addEventListener("pointercancel", endDrag);

    return () => {
      track.removeEventListener("pointerdown", onPointerDown);
      track.removeEventListener("pointermove", onPointerMove);
      track.removeEventListener("pointerup", endDrag);
      track.removeEventListener("pointercancel", endDrag);
      track.style.scrollSnapType = "";
    };
  }, [hasMultiple, locationCount, trackRef, updateScrollEdges]);

  return {
    canScrollLeft,
    canScrollRight,
    isDragging,
    onTrackScroll
  };
}
